import React from 'react';
import classNames from 'classnames';
import styles from './Carousel.module.scss';
import useCarousel from 'LearningManager/hooks/Carousel.context';
import { Icon } from 'Components';

interface CarouselProps<T> {
	data: T[];
	label?: string;
	itemsPerPage?: number;
	renderItem: (item: T, index: number) => React.ReactNode;
	onPrev?: () => void;
	onNext?: () => void;
}

interface CarouselDotsProps {
	totalPages: number;
	currentPage: number;
	onDotClick: (page: number) => void;
}

const CarouselDots: React.FC<CarouselDotsProps> = ({
	totalPages,
	currentPage,
	onDotClick
}) => {
	if (totalPages <= 1) return null;

	return (
		<div className={styles.dots}>
			{Array.from({ length: totalPages }).map((_, index) => (
				<button
					key={index}
					className={classNames(styles.dot, {
						[styles['dot--active']]: index === currentPage
					})}
					onClick={() => onDotClick(index)}
					aria-label={`Go to page ${index + 1}`}
				/>
			))}
		</div>
	);
};

function Carousel<T>({
	data,
	label,
	itemsPerPage = 4,
	renderItem,
	onPrev,
	onNext
}: CarouselProps<T>): React.ReactElement {
	const {
		currentPage,
		totalPages,
		handlePrev,
		handleNext,
		goToPage,
		getVisibleRange,
		canGoNext,
		canGoPrev,
		direction
	} = useCarousel({ totalItems: data.length, itemsPerPage });

	const { start, end } = getVisibleRange();
	const visibleItems = data.slice(start, end);

	const handlePrevClick = () => {
		handlePrev();
		onPrev?.();
	};

	const handleNextClick = () => {
		handleNext();
		onNext?.();
	};

	return (
		<div className={styles.root}>
			<div className={styles.topContainer}>
				{label && <div className={styles.label}>{label}</div>}
				{totalPages > 1 && (
					<div className={styles.controlBtns}>
						<button
							className={classNames(styles.btn, {
								[styles['btn--disabled']]: !canGoPrev
							})}
							onClick={handlePrevClick}
							disabled={!canGoPrev}
						>
							<Icon
								type="chevronUp"
								style={{ rotate: '-90deg' }}
							/>
						</button>

						<button
							className={classNames(styles.btn, {
								[styles['btn--disabled']]: !canGoNext
							})}
							onClick={handleNextClick}
							disabled={!canGoNext}
						>
							<Icon
								type="chevronUp"
								style={{ rotate: '90deg' }}
							/>
						</button>
					</div>
				)}
			</div>
			<div
				className={classNames(styles.container, {
					[styles['container--left']]: direction === 'left',
					[styles['container--right']]: direction === 'right'
				})}
				key={currentPage}
			>
				{visibleItems.map((item, index) =>
					renderItem(item, start + index)
				)}
			</div>

			<CarouselDots
				totalPages={totalPages}
				currentPage={currentPage}
				onDotClick={goToPage}
			/>
		</div>
	);
}

export type { CarouselProps };
export default Carousel;
